/**
 * combat_telegraph.ts — the client's view of NPC windups (combat wave).
 *
 * The server's `npc_strike` row IS the telegraph: it appears when an NPC
 * begins a blow and disappears at the snapshot instant (landed or whiffed) or
 * when a stagger cancels it. WildsModule owns the subscription and feeds
 * these maps; every renderer (creatures, versonas, the hunter) reads them by
 * attacker key so the tell and the lunge are drawn from one source.
 */
import { LUNGE_MS, feel } from './combat_feel.js';

export interface Windup { start: number; ms: number; targetMe: boolean }

/** attackerKey → the windup in progress (client clock, ms). */
export const windups = new Map<string, Windup>();
/** attackerKey → when its last windup ended (the lunge plays from here). */
export const lunges = new Map<string, number>();

export function telegraphBegin(key: string, windupMs: number, targetMe: boolean, now: number): void {
  windups.set(key, { start: now, ms: windupMs, targetMe });
  feel.windups++;
}

export function telegraphEnd(key: string, now: number): void {
  if (windups.delete(key)) lunges.set(key, now);
}

/** The lunge start for a key, or −1 once it has played out (and pruned). */
export function lungeAt(key: string, now: number): number {
  const t = lunges.get(key);
  if (t === undefined) return -1;
  if (now > t + LUNGE_MS) { lunges.delete(key); return -1; }
  return t;
}

/** Drop windups whose server row should long since have resolved (a missed
 *  delete after a reconnect must not leave a permanent tell). */
export function pruneTelegraphs(now: number): void {
  for (const [k, w] of windups) if (now > w.start + w.ms + 2000) windups.delete(k);
}

export function activeWindupCount(): number { return windups.size; }

/** victimKey ('versona:<id>') → when its hit reaction ends (rig wave: the
 *  near-skinned layer plays Hit_Reaction; the crowd body just flinches). */
export const hurtUntil = new Map<string, number>();
/** victimKey → when the PLAYER last struck them (client clock, ms) — the
 *  threat rung of attention priority (versona_gaze, 2026-09-15). Blows from
 *  creatures or other versonas do not land here: the victim's eyes go to the
 *  attacker, and only the player's strikes make the player the attacker. */
export const struckByPlayerAt = new Map<string, number>();
export function markStruckByPlayer(key: string, now: number): void { struckByPlayerAt.set(key, now); }
export function lastStruckByPlayer(key: string): number { return struckByPlayerAt.get(key) ?? -Infinity; }
export const HURT_MS = 700;
export function markHurt(key: string, now: number): void { hurtUntil.set(key, now + HURT_MS); }
export function isHurt(key: string, now: number): boolean {
  const t = hurtUntil.get(key);
  if (t === undefined) return false;
  if (now > t) { hurtUntil.delete(key); return false; }
  return true;
}
